import React from 'react';
import ResponsiveECharts from './ResponsiveECharts';
import { TAILLE_TEXTE, COULEUR_TEXTE } from './echartsTheme';
import { formatDateFr, toTimestamp } from '../utils/echartsSeries';
import { STATS, Puce, motifSvg } from './CourbesStatChart';

// Une couleur par élément suivi, stable quel que soit le nombre d'éléments
// cochés : la couleur d'un élément ne doit pas changer selon sa position.
const COULEURS = {
  mais_grain: '#92D050',
  tournesol: '#F4D9B6',
  // Reprises du Tableau de bord, comme demandé — je les avais changées par
  // erreur la fois précédente, remises à l'identique.
  vaches_laitieres: '#6C2B72',
  cochons: '#3E07AD',
};

/**
 * Tension sur la ressource en eau, au fil de la campagne.
 *
 * Même lecture que le besoin en eau (axe temporel, une série par élément),
 * avec deux différences demandées :
 *  - pas de bande d'écart-type ;
 *  - séries empilées, avec l'aire sous la courbe remplie, pour lire la
 *    tension cumulée de tous les éléments cochés.
 *
 * Sous le graphique, un sélecteur de statistique (mêmes pastilles que
 * CourbesStatChart) : une seule statistique à la fois, cocher une pastille
 * décoche automatiquement la précédente. Moyen à l'ouverture.
 *
 * @param data [{ cle, label, points: [{ date, eau_moy, eau_q75, eau_q90, eau_max }] }]
 * @param statParDefaut statistique affichée à l'ouverture ('moy', 'q75', 'q90', 'max')
 */
export default function TensionChart({ data = [], statParDefaut = 'moy' }) {
  const [stat, setStat] = React.useState(statParDefaut);

  if (data.length === 0) {
    return (
      <p className="tdb-chargement">
        Sélectionnez au moins un élément à afficher.
      </p>
    );
  }

  const styleTrait = STATS.find((st) => st.cle === stat)?.echarts ?? 'solid';

  const series = data.map((element) => {
    const couleur = COULEURS[element.cle] ?? '#8E3A94';
    return {
      name: element.label,
      type: 'line',
      // Toutes les séries partagent la même pile : les aires s'additionnent.
      stack: 'tension',
      symbol: 'none',
      smooth: false,
      // Même motif de trait que la statistique dans CourbesStatChart.
      lineStyle: { width: 2, color: couleur, type: styleTrait },
      itemStyle: { color: couleur },
      areaStyle: { color: couleur, opacity: 0.75 },
      data: (element.points ?? []).map((p) => [toTimestamp(p.date), p[`eau_${stat}`]]),
    };
  });

  const option = {
    textStyle: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE },
    tooltip: {
      trigger: 'axis',
      textStyle: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE },
      formatter: (params) => {
        const date = formatDateFr(params[0].value[0]);
        let html = `${date}<br/>`;
        let cumul = 0;
        params.forEach((p) => {
          cumul += Number(p.value[1]);
          html += `${p.marker} ${p.seriesName} : ${Number(p.value[1]).toFixed(1)} m³/j<br/>`;
        });
        html += `<b>Total : ${cumul.toFixed(1)} m³/j</b>`;
        return html;
      },
    },
    legend: {
      bottom: 0,
      textStyle: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE },
    },
    grid: { left: 24, right: 24, top: 56, bottom: 60, containLabel: true },
    xAxis: {
      type: 'time',
      axisLabel: { formatter: formatDateFr, fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE },
      axisLine: { lineStyle: { color: COULEUR_TEXTE } },
    },
    yAxis: {
      type: 'value',
      // Nom horizontal au-dessus de l'axe, comme partout ailleurs.
      name: 'Tension (m³/j)',
      nameLocation: 'end',
      nameRotate: 0,
      nameGap: 24,
      nameTextStyle: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE, fontWeight: 600, align: 'left' },
      axisLabel: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE },
      axisLine: { show: true, lineStyle: { color: COULEUR_TEXTE } },
      splitLine: { lineStyle: { color: '#e2e5ea' } },
    },
    series,
  };

  // Hauteur fixe : `flex: 1 1 auto` ne marchait que quand la page n'avait
  // qu'une seule card (règle .oad-card:only-child qui étire la card). Avec la
  // card du total en dessous, la card n'est plus étirée, le conteneur n'a
  // plus de hauteur définie et le graphique se retrouvait tassé.
  return (
    <div>
      <ResponsiveECharts option={option} style={{ height: 460 }} />

      {/* Sélecteur de statistique : choix unique (comportement de bouton radio). */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#000' }}>Statistique :</span>
        {STATS.map((st) => {
          const actif = st.cle === stat;
          return (
            <Puce key={st.cle} actif={actif} onClick={() => setStat(st.cle)}>
              <svg width="30" height="8" aria-hidden="true">
                <line
                  x1="0"
                  y1="4"
                  x2="30"
                  y2="4"
                  stroke={actif ? '#000' : '#d0d4d9'}
                  strokeWidth="2"
                  strokeDasharray={motifSvg(st.echarts)}
                />
              </svg>
              {st.label}
            </Puce>
          );
        })}
      </div>
    </div>
  );
}
